import { normalizeCrmStatus, type CrmStatusKey } from '@isbd/shared';
import { supabase } from '@/lib/supabase';

export type CrmLead = {
  id: string;
  contact_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  location: string;
  company: string;
  opportunity: string;
  budget: number;
  status: CrmStatusKey;
  source: string;
  priority: string;
  notes: string;
  project_id: string | null;
  inquiry_id?: string | null;
};

function stripInquiryPrefix(notes: string): string {
  return notes.replace(/^__inq__:[0-9a-f-]{36}\n?/i, '');
}

export function inquiryIdFromLead(lead: Pick<CrmLead, 'inquiry_id' | 'notes'>): string | null {
  if (lead.inquiry_id) return lead.inquiry_id;
  const m = lead.notes?.match(/^__inq__:([0-9a-f-]{36})/i);
  return m?.[1] ?? null;
}

export function displayNotes(lead: Pick<CrmLead, 'notes'>): string {
  return stripInquiryPrefix(lead.notes ?? '');
}

export function isMarketplaceSource(source: string): boolean {
  return source.trim().toLowerCase() === 'marketplace';
}

export function leadDisplayName(lead: Pick<CrmLead, 'first_name' | 'last_name' | 'contact_name'>): string {
  const n = `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim();
  return n || lead.contact_name;
}

function splitName(full: string): { first_name: string; last_name: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0]!, last_name: '' };
  return { first_name: parts[0]!, last_name: parts.slice(1).join(' ') };
}

/** Importe les demandes Marketplace absentes du CRM (idempotent). */
export async function syncMarketplaceInquiriesToCrm(userId: string): Promise<string | null> {
  const { data: inquiries, error: iErr } = await supabase
    .from('marketplace_inquiries')
    .select('id, visitor_name, visitor_email, residence, phone, whatsapp, message, product_id')
    .eq('seller_id', userId)
    .order('created_at', { ascending: false });

  if (iErr) return iErr.message;

  const productIds = [...new Set((inquiries ?? []).map((r) => String(r.product_id)).filter(Boolean))];
  const productMap = new Map<string, { name: string; price: number }>();
  if (productIds.length > 0) {
    const { data: products } = await supabase.from('marketplace_products').select('id, name, price').in('id', productIds);
    for (const p of products ?? []) {
      productMap.set(String(p.id), { name: String(p.name ?? ''), price: Number(p.price ?? 0) });
    }
  }

  const { data: leads, error: lErr } = await supabase
    .from('crm_leads')
    .select('id, notes, source')
    .eq('user_id', userId);

  if (lErr) return lErr.message;

  const { data: leadsWithInq } = await supabase
    .from('crm_leads')
    .select('id, inquiry_id')
    .eq('user_id', userId)
    .not('inquiry_id', 'is', null);

  const linked = new Set<string>();
  for (const row of leads ?? []) {
    const id = inquiryIdFromLead(row as CrmLead);
    if (id) linked.add(id);
  }
  for (const row of leadsWithInq ?? []) {
    if (row.inquiry_id) linked.add(String(row.inquiry_id));
  }

  for (const raw of inquiries ?? []) {
    const inqId = String(raw.id);
    if (linked.has(inqId)) continue;
    const product = productMap.get(String(raw.product_id));
    const productName = product?.name ?? '';
    const price = product?.price ?? 0;
    const visitorName = String(raw.visitor_name || raw.visitor_email || 'Prospect Marketplace');
    const { first_name, last_name } = splitName(visitorName);
    const phone = String(raw.phone || raw.whatsapp || '');

    const base = {
      user_id: userId,
      contact_name: visitorName,
      first_name,
      last_name,
      email: String(raw.visitor_email || ''),
      phone,
      location: String(raw.residence || ''),
      company: '',
      opportunity: productName,
      budget: price,
      status: 'nouveau',
      source: 'marketplace',
      priority: 'normal',
      notes: String(raw.message || ''),
      project_id: null as string | null,
    };

    const { error: insErr } = await supabase.from('crm_leads').insert({
      ...base,
      inquiry_id: inqId,
    });

    if (insErr) {
      // Colonnes étendues absentes → insert minimal
      const minimal = {
        user_id: userId,
        contact_name: visitorName,
        company: String(raw.residence || ''),
        opportunity: productName,
        budget: price,
        status: 'nouveau',
        source: 'marketplace',
        priority: 'normal',
        notes: `__inq__:${inqId}\n${raw.message || ''}`,
        project_id: null as string | null,
      };
      const { error: fallbackErr } = await supabase.from('crm_leads').insert(minimal);
      if (fallbackErr) return fallbackErr.message;
    }
  }

  return null;
}

export async function loadCrmLeads(userId: string): Promise<{ leads: CrmLead[]; error: string | null }> {
  const { data, error } = await supabase
    .from('crm_leads')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) return { leads: [], error: error.message };

  const leads = ((data ?? []) as Record<string, unknown>[]).map((row) => {
    const contact_name = String(row.contact_name ?? '');
    const split = splitName(contact_name);
    return {
      id: String(row.id),
      contact_name,
      first_name: String(row.first_name || split.first_name),
      last_name: String(row.last_name || split.last_name),
      email: String(row.email ?? ''),
      phone: String(row.phone ?? ''),
      location: String(row.location ?? ''),
      company: String(row.company ?? ''),
      opportunity: String(row.opportunity ?? ''),
      budget: Number(row.budget ?? 0),
      status: normalizeCrmStatus(String(row.status ?? 'nouveau')),
      source: String(row.source ?? ''),
      priority: String(row.priority ?? 'normal'),
      notes: String(row.notes ?? ''),
      project_id: (row.project_id as string | null) ?? null,
      inquiry_id: (row.inquiry_id as string | null | undefined) ?? null,
    };
  });

  return { leads, error: null };
}
